import { c as _c } from "react/compiler-runtime";
import { Alert, Button, CircularProgress, InfoCircleFillIcon, NO_TRANSLATE_CLASS, Text } from "@flat-ui";
import { Box, Stack } from "@mui-wrapper";
import { formatUserRoleType } from "@utils/data-display";
import { getPrettyName } from "@utils/user-utils";
import { useEffect } from "react";

/**
 * The name of the member in a sentence of a step. It is user data and therefore not translated, and
 * it keeps the weight of the text around it, which the global `var` style would reset otherwise.
 */
export function MemberName(t0) {
  const $ = _c(5);
  const {
    member
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      fontWeight: "inherit"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== member) {
    t2 = getPrettyName(member);
    $[1] = member;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Box component="var" sx={t1}>{t2}</Box>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** Tells the user that there is nothing to do in a step, since the member has nothing to give up */
export function NothingToDoAlert(t0) {
  const $ = _c(2);
  const {
    message
  } = t0;
  let t1;
  if ($[0] !== message) {
    t1 = <Box data-testid="remove-xg-seat-empty"><Alert variant="success" title={message} /></Box>;
    $[0] = message;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/** An action the user can take on an item of a step, or on all of them at once */

/** An item listed in a step, e.g. a team the member belongs to */

/** Returns what the member is in a project, and in which group, as a project is listed */
export function getMemberProjectDetails({
  project,
  group
}) {
  return <>
      {project.role ? formatUserRoleType(project.role) : ""} in{" "}
      <var>{group.name}</var>
    </>;
}

/** Shows why no action can be taken on an item */
function BlockReason(t0) {
  const $ = _c(5);
  const {
    reason
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 0.5,
      alignItems: "center"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <InfoCircleFillIcon sx={{
      width: "1rem",
      height: "1rem",
      flexShrink: 0,
      color: "text.secondary"
    }} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      color: "text.secondary"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== reason) {
    t4 = <Stack direction="row" sx={t1}>{t2}<Text variant="bodyS" sx={t3}>{reason}</Text></Stack>;
    $[3] = reason;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

/** The buttons of an item, a spinner while one of them is in progress */
function ListItemActions(t0) {
  const $ = _c(9);
  const {
    item,
    isPending,
    isBusy
  } = t0;
  if (isPending) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CircularProgress size="1.5rem" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 1,
      flexShrink: 0
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isBusy || $[3] !== item.actions) {
    let t3;
    if ($[5] !== isBusy) {
      t3 = action => <Button key={action.label} size="s" variant={action.variant ?? "secondary"} disabled={isBusy} onClick={action.onClick}>{action.label}</Button>;
      $[5] = isBusy;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = item.actions.map(t3);
    $[2] = isBusy;
    $[3] = item.actions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <Stack direction="row" sx={t1}>{t2}</Stack>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/** An item of a step, with its name, what the member is in it and the actions to take on it */
function ListItem(props) {
  const $ = _c(20);
  const {
    item
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      gap: 2,
      py: 1.5,
      alignItems: "center",
      justifyContent: "space-between",
      borderBottom: 1,
      borderColor: "divider"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      minWidth: 0,
      gap: 0.25
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      overflowWrap: "anywhere"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item.name) {
    t3 = <Text variant="heading14" className={NO_TRANSLATE_CLASS} sx={t2}>{item.name}</Text>;
    $[3] = item.name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      color: "text.secondary"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== item.details) {
    t5 = <Text variant="bodyS" sx={t4}>{item.details}</Text>;
    $[6] = item.details;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== item.blockReason) {
    t6 = item.blockReason && <BlockReason reason={item.blockReason} />;
    $[8] = item.blockReason;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t5 || $[12] !== t6) {
    t7 = <Stack sx={t1}>{t3}{t5}{t6}</Stack>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== item.blockReason || $[15] !== props) {
    t8 = !item.blockReason && <ListItemActions {...props} />;
    $[14] = item.blockReason;
    $[15] = props;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <Stack component="li" direction="row" data-testid="remove-xg-seat-item" sx={t0}>{t7}{t8}</Stack>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
/**
 * Lists the items of a step of the dialog that takes a Sphere XG seat away, e.g. the teams that give
 * the member an editor role, with the actions to take on each of them and on all at once. While any
 * action is in progress all the others are blocked, so that they cannot get in each other's way.
 */
export function RemoveXgSeatList(t0) {
  const $ = _c(35);
  const {
    status,
    description,
    emptyMessage,
    failedMessage,
    items,
    pendingIds,
    bulkActions,
    onResolvedChange,
    isGoOnHintShown: t1
  } = t0;
  const isGoOnHintShown = t1 === undefined ? true : t1;
  const isBusy = pendingIds.size > 0;
  const isResolved = status === "loaded" && !items.length;
  let t2;
  let t3;
  if ($[0] !== isResolved || $[1] !== onResolvedChange) {
    t2 = () => {
      onResolvedChange(isResolved);
    };
    t3 = [isResolved, onResolvedChange];
    $[0] = isResolved;
    $[1] = onResolvedChange;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== items) {
    t4 = items.filter(_temp);
    $[4] = items;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const actionableCount = t4.length;
  if (status === "loading") {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Stack sx={{
        alignItems: "center",
        py: 3
      }}><CircularProgress size="2rem" dataTestId="remove-xg-seat-loading" /></Stack>;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  if (status === "failed") {
    let t5;
    if ($[7] !== failedMessage) {
      t5 = <Text variant="bodyM">{failedMessage}</Text>;
      $[7] = failedMessage;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (!items.length) {
    let t5;
    if ($[9] !== emptyMessage) {
      t5 = <NothingToDoAlert message={emptyMessage} />;
      $[9] = emptyMessage;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      gap: 1
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== description) {
    t6 = <Text variant="bodyM">{description}</Text>;
    $[12] = description;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isGoOnHintShown) {
    t7 = isGoOnHintShown && <Text variant="bodyS" sx={{
      color: "text.secondary"
    }}>You can go on once nothing is listed here anymore.</Text>;
    $[14] = isGoOnHintShown;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== actionableCount || $[17] !== bulkActions || $[18] !== isBusy) {
    t8 = actionableCount > 1 && <Stack direction="row" sx={{
      gap: 1,
      justifyContent: "flex-end",
      flexWrap: "wrap"
    }}>{bulkActions.map(action => <Button key={action.label} size="s" variant={action.variant ?? "secondary"} disabled={isBusy} onClick={action.onClick}>{action.label}</Button>)}</Stack>;
    $[16] = actionableCount;
    $[17] = bulkActions;
    $[18] = isBusy;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      m: 0,
      p: 0,
      listStyle: "none"
    };
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== isBusy || $[22] !== items || $[23] !== pendingIds) {
    let t11;
    if ($[25] !== isBusy || $[26] !== pendingIds) {
      t11 = item => <ListItem key={item.id} item={item} isPending={pendingIds.has(item.id)} isBusy={isBusy} />;
      $[25] = isBusy;
      $[26] = pendingIds;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    t10 = items.map(t11);
    $[21] = isBusy;
    $[22] = items;
    $[23] = pendingIds;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <Stack component="ul" sx={t9}>{t10}</Stack>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t12 = <Stack sx={t5}>{t6}{t7}{t8}{t11}</Stack>;
    $[30] = t11;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}
function _temp(t0) {
  const {
    blockReason
  } = t0;
  return !blockReason;
}